import type { Record, Shape, Transform } from '@servicenow/sdk-build-core'
import type { RagInputType, ToolInputField } from './aia-types'
import type { SearchTypeConfig } from '../../api/search-type-config'
import { CHANNEL_SYS_ID_TO_NAME_MAP, OOB_TOOL_TYPE_TO_SYS_ID_MAP, PUBLIC_ACCESS_ROLE_SYS_ID } from './constants'
import { NowIdShape, NowIncludeShape } from '@servicenow/sdk-build-plugins'

/**
 * Helper function to extract sys_id from either a string or Record reference
 */
export const extractSysId = (v: Shape, defaultValue = ''): string => {
    if (!v) {
        return defaultValue
    }
    if (v.isRecord()) {
        return v.getId().getValue()
    }
    return v.ifString()?.getValue() || defaultValue
}

/**
 * Helper function to extract target_document for specific tool types
 * @param toolType - The current tool type
 * @param expectedTypes - The expected type(s) to match
 * @param toolRecord - The tool record to extract from
 * @returns The target_document value or undefined
 */
const getTargetDocumentForType = (
    toolType: string | undefined,
    expectedTypes: string | string[],
    toolRecord: Record | undefined
): string | undefined => {
    const types = Array.isArray(expectedTypes) ? expectedTypes : [expectedTypes]
    return types.includes(toolType ?? '') ? toolRecord?.get('target_document')?.ifString()?.getValue() : undefined
}

/**
 * Helper function to conditionally include a value only if it differs from the default
 * Returns undefined if the value matches the default, otherwise returns the value
 */
const includeIfNotDefault = <T>(value: T, defaultValue: T): T | undefined => {
    // Handle array comparison
    if (Array.isArray(value) && Array.isArray(defaultValue)) {
        if (value.length === 0 && defaultValue.length === 0) {
            return undefined
        }
        if (JSON.stringify(value) === JSON.stringify(defaultValue)) {
            return undefined
        }
        return value
    }

    // Handle object comparison
    if (typeof value === 'object' && value !== null && typeof defaultValue === 'object' && defaultValue !== null) {
        if (JSON.stringify(value) === JSON.stringify(defaultValue)) {
            return undefined
        }
        return value
    }

    // Handle primitive comparison
    if (value === defaultValue) {
        return undefined
    }

    return value
}

/**
 * Helper function to transform ServiceNow CRUD inputs JSON to ToolInputType format
 */
const transformCrudInputs = (inputsArray: unknown[]): unknown => {
    // Find the crudInputs object
    const crudInput = inputsArray.find(
        (input: unknown): input is { name: string; value: unknown } =>
            input !== null &&
            typeof input === 'object' &&
            'name' in input &&
            (input as { name: string }).name === 'crudInputs'
    )

    if (!crudInput || typeof crudInput !== 'object' || !('value' in crudInput)) {
        return inputsArray // Return as-is if no crudInputs found
    }

    const crudValue = crudInput.value as
        | {
              operation?: string
              table?: { value?: string }
              fieldValues?: unknown[]
              query?: string // ServiceNow uses 'query' field
              queryCondition?: string // Our API uses 'queryCondition'
              returnFields?: Array<{ name: string }>
              limit?: number | string
              orderBy?: string
              sortType?: string
          }
        | undefined
    if (!crudValue || typeof crudValue !== 'object') {
        return inputsArray
    }

    // Extract operation name
    const operationName = crudValue.operation
    if (!operationName) {
        return inputsArray
    }

    // Extract table
    const table = crudValue.table?.value
    if (!table) {
        return inputsArray
    }

    // Extract fieldValues from crudInputs to get mappedToColumn and type info
    const fieldValues = (crudValue.fieldValues || []) as Array<{
        field?: { name?: string; type?: string }
        value?: { fieldValue?: string }
    }>

    // Create a map of field name to its metadata
    const fieldMetadataMap = new Map<string, { mappedToColumn?: string; type?: string }>()
    for (const fieldValue of fieldValues) {
        const fieldName = fieldValue.field?.name
        const fieldType = fieldValue.field?.type
        if (fieldName) {
            const metadata: { mappedToColumn?: string; type?: string } = {
                mappedToColumn: fieldName, // The field name is the column name
            }
            if (fieldType) {
                metadata.type = fieldType
            }
            fieldMetadataMap.set(fieldName, metadata)
        }
    }

    // Extract input fields (all inputs except crudInputs) and enrich with metadata
    const inputFields = inputsArray
        .filter((input: unknown) => {
            if (!input || typeof input !== 'object' || !('name' in input)) {
                return false
            }
            return (input as { name: string }).name !== 'crudInputs'
        })
        .map((input: unknown) => {
            const inputObj = input as { name?: string; description?: string; mandatory?: boolean }
            const fieldName = inputObj.name || ''
            const metadata = fieldMetadataMap.get(fieldName)

            return {
                name: fieldName,
                description: inputObj.description || '',
                mandatory: inputObj.mandatory ?? false,
                ...(metadata?.mappedToColumn !== undefined && { mappedToColumn: metadata.mappedToColumn }),
                ...(metadata?.type !== undefined && metadata.type !== 'string' && { type: metadata.type }),
            }
        })

    // Build the ToolInputType object with all CRUD-specific fields
    const result: {
        operationName: string
        table: string
        inputFields: unknown[]
        queryCondition?: string
        returnFields?: Array<{ name: string }>
        limit?: number | string
        orderBy?: string
        sortType?: string
    } = {
        operationName,
        table,
        inputFields,
    }

    // Include optional CRUD fields if they exist
    // Map 'query' from ServiceNow to 'queryCondition' in our API
    const queryValue = crudValue.queryCondition ?? crudValue.query
    if (queryValue !== undefined) {
        result.queryCondition = queryValue
    }
    if (crudValue.returnFields !== undefined && Array.isArray(crudValue.returnFields)) {
        // Clean up returnFields - only keep essential fields (name, type, referenceConfig)
        // Remove ServiceNow's internal metadata fields
        result.returnFields = crudValue.returnFields.map(
            (field: {
                name: string
                type?: string
                referenceTable?: string | null
                referenceKey?: string
                referenceTableDefaultField?: string
            }) => {
                const cleanField: {
                    name: string
                    type?: string
                    referenceConfig?: { table: string; field: string }
                } = {
                    name: field.name,
                }
                // Only include type if it exists, is not empty, and is not the default 'string'
                if (field.type && field.type !== '' && field.type !== 'string') {
                    cleanField.type = field.type
                }
                // Include referenceConfig if this is a reference field
                if (field.referenceTable && field.referenceTable !== null) {
                    cleanField.referenceConfig = {
                        table: field.referenceTable,
                        field: field.referenceKey || field.referenceTableDefaultField || 'sys_id',
                    }
                }
                return cleanField
            }
        )
    }
    if (crudValue.limit !== undefined) {
        const parsedLimit = Number(crudValue.limit)
        result.limit = Number.isNaN(parsedLimit) ? crudValue.limit : parsedLimit
    }
    if (crudValue.orderBy !== undefined) {
        result.orderBy = crudValue.orderBy
    }
    if (crudValue.sortType !== undefined && crudValue.sortType !== '') {
        result.sortType = crudValue.sortType
    }

    return result
}

/**
 * Maps internal ServiceNow tool type back to user-facing Fluent API type.
 * Reverse of mapToolTypeToInternal() in ai-agent-shapes-helper.ts.
 */
const mapToolTypeToExternal = (toolType: string | undefined): string | undefined => {
    if (toolType === 'rag') {
        return 'search_retrieval'
    }
    return toolType
}

/**
 * Reconstructs a single value/label object from a raw input entry.
 * Returns a ValueLabelType object with value and label properties.
 */
const reconstructValueLabel = (input: { [key: string]: unknown } | undefined): { value: string; label: string } => {
    const value = (input?.['value'] as string) || ''
    const label = (input?.['label'] as string) || value
    return { value, label }
}

/**
 * Reconstructs an array of value/label objects from a raw input entry.
 * Handles label as either an array (platform/AI Studio format) or a comma-separated string (legacy).
 * Returns ValueLabelType objects with value and label properties.
 */
const reconstructValueLabelArray = (
    input: { [key: string]: unknown } | undefined
): { value: string; label: string }[] | undefined => {
    const values = input?.['value'] as string[] | undefined
    if (!values) {
        return undefined
    }

    const rawLabel = input?.['label']
    if (!rawLabel) {
        return values.map((value) => {
            return { value, label: value }
        })
    }

    // Handle both array labels (platform format) and comma-separated string labels (legacy)
    const labels = Array.isArray(rawLabel) ? (rawLabel as string[]) : (rawLabel as string).split(', ')
    return values.map((value, index) => {
        const label = labels[index] || value
        return { value, label }
    })
}

/**
 * Transforms RAG tool inputs array from ServiceNow JSON format back to RagInputType.
 * Reverse operation of generateRagInputs() in tool-input-helper.ts.
 *
 * Iterates the inputs array once to collect all named entries, then builds the result object.
 *
 * Input format (from sn_aia_agent_tool_m2m.inputs):
 *   - search_type: { value: 'keyword' | 'semantic' | 'hybrid' }
 *   - query: { description: '<query text>' }  (auto-generated, not extracted)
 *   - search_profile: { value: '<profile_id>', label: '<display>' }
 *   - sources: { value: ['<table>'], label: ['<display>'] }
 *   - fields: { value: ['<table.field>'], label: ['<display>'] }  (optional)
 *   - search_results_limit: { value: '<number>' }  (can be empty string)
 *   - semantic_index_names: { value: ['<field>'] }  (only for semantic/hybrid)
 *   - document_match_threshold: { value: <number> }  (present for all types; 0 = default)
 *
 * Note: The query field is auto-generated during toRecord and is not part of the user-facing RagInputType.
 */
const transformRagInputs = (inputsArray: unknown[]): RagInputType | undefined => {
    const inputs = inputsArray as { [key: string]: unknown }[]

    // Single-pass: collect all named inputs into a map
    const inputsByName: { [key: string]: { [key: string]: unknown } } = {}
    for (const input of inputs) {
        const name = input['name'] as string | undefined
        if (name) {
            inputsByName[name] = input
        }
    }

    // Validate required field: search_type
    // Note: query field is auto-generated in generateRagInputs, so we skip it here
    const searchTypeValue = inputsByName['search_type']?.['value'] as string | undefined
    if (!searchTypeValue) {
        return undefined
    }

    // Extract search_profile - reconstruct { value, label } object
    const searchProfile = reconstructValueLabel(inputsByName['search_profile'])

    // Extract sources - reconstruct { value, label } objects if label exists
    const sources = reconstructValueLabelArray(inputsByName['sources']) ?? []

    // Extract optional fields - reconstruct { value, label } objects if label exists
    const fields = reconstructValueLabelArray(inputsByName['fields'])

    // Extract optional search_results_limit (string in DB, convert to number)
    const limitStr = inputsByName['search_results_limit']?.['value'] as string | undefined
    const searchResultsLimit = limitStr && limitStr !== '' ? Number(limitStr) : undefined

    // Build SearchTypeConfig
    let searchType: SearchTypeConfig

    if (searchTypeValue === 'semantic' || searchTypeValue === 'hybrid') {
        // Extract semantic-specific fields - reconstruct { value, label } objects if label exists
        const semanticIndexes = reconstructValueLabelArray(inputsByName['semantic_index_names']) ?? []

        // Extract document_match_threshold (for semantic/hybrid types)
        const thresholdRaw = inputsByName['document_match_threshold']?.['value']
        const documentMatchThreshold =
            thresholdRaw !== undefined && thresholdRaw !== '' ? Number(thresholdRaw) : undefined

        // Build flattened semantic/hybrid SearchTypeConfig
        searchType = {
            type: searchTypeValue,
            semanticIndexes,
            ...(documentMatchThreshold !== undefined && { documentMatchThreshold }),
        }
    } else {
        searchType = { type: 'keyword' as const }
    }

    // Build and validate result
    const result: RagInputType = {
        searchType,
        searchProfile,
        sources,
    }

    if (fields && fields.length > 0) {
        result.fields = fields
    }

    if (searchResultsLimit !== undefined && !Number.isNaN(searchResultsLimit)) {
        result.searchResultsLimit = searchResultsLimit
    }

    return result
}

/**
 * Type representing CRUD input fields
 */
interface CrudInputFields {
    queryCondition?: string
    limit?: string
    orderBy?: string
    sortType?: string
}

/**
 * Helper function to parse CRUD inputs fields from inputs Shape
 * Returns an object with queryCondition, limit, orderBy, and sortType fields
 */
export const parseCrudInputsFields = (inputsShape: Shape | undefined): CrudInputFields => {
    if (!inputsShape) {
        return {}
    }

    // Use Shape's parseJson() utility method instead of manual JSON.parse()
    const inputsArray = inputsShape.ifString()?.parseJson()?.getValue()
    if (!inputsArray || !Array.isArray(inputsArray)) {
        return {}
    }

    const crudInputs = inputsArray.find((input: unknown): input is ToolInputField => {
        return (
            input !== null &&
            typeof input === 'object' &&
            'name' in input &&
            (input as ToolInputField).name === 'crudInputs'
        )
    })
    const crudValue = crudInputs?.value as CrudInputFields | undefined

    if (!crudValue || typeof crudValue !== 'object') {
        return {}
    }

    const result: CrudInputFields = {}
    if (crudValue.queryCondition !== undefined) {
        result.queryCondition = crudValue.queryCondition
    }
    if (crudValue.limit !== undefined) {
        result.limit = crudValue.limit
    }
    if (crudValue.orderBy !== undefined) {
        result.orderBy = crudValue.orderBy
    }
    if (crudValue.sortType !== undefined) {
        result.sortType = crudValue.sortType
    }
    return result
}

/**
 * Helper function to map M2M records to tool objects with a flattened structure
 */
export const mapToolsFromM2M = async (toolM2Ms: Record[], toolRecords: Record[], transform: Transform) => {
    return Promise.all(
        toolM2Ms.map(async (m2m) => {
            // Get the tool record - try multiple approaches
            // 1. Try to get as a record reference directly
            let toolRecord = m2m.get('tool')?.ifRecord()

            // 2. If not found, try to match by id from descendants
            const toolId = m2m.get('tool')?.ifString()?.getValue()
            if (!toolRecord && toolId) {
                toolRecord = toolRecords.find((t) => t.getId().getValue() === toolId)
            }

            // Pre-compute type value
            // OOB sys_id lookup takes priority over the DB type field, because the DB may store
            // a different value (e.g. file_upload OOB tool stores 'rag' in the type field)
            let toolType: string | undefined
            if (toolId) {
                for (const [type, sysId] of Object.entries(OOB_TOOL_TYPE_TO_SYS_ID_MAP)) {
                    if (sysId === toolId) {
                        toolType = type
                        break
                    }
                }
            }
            // Fall back to type from tool record if not an OOB tool
            if (!toolType) {
                toolType = toolRecord?.get('type')?.ifString()?.getValue()
            }

            // Extract script to separate file for script tools only
            // CRUD tool scripts are auto-generated; OOB and other typed tools don't have scripts
            let script: NowIncludeShape | undefined
            if (toolType === 'script' && toolRecord?.has('script')) {
                const scriptValue = toolRecord.get('script')?.ifString()?.getValue()
                if (scriptValue && scriptValue !== '') {
                    script = await NowIncludeShape.fromRecord(toolRecord, toolRecord.get('script'), transform)
                }
            }

            // Pre-compute numeric fields that need empty-string guarding before transform
            const maxAutoExecutionsValue =
                m2m.get('max_auto_executions')?.ifString()?.ifNotEmpty()?.toNumber()?.getValue() ?? 10

            // Use transform to build the tool object
            return m2m.transform(({ $ }) => ({
                // Common properties from M2M record only (no fallback to tool record)
                active: $.from('active').toBoolean().def(true),
                description: $.from('description'),
                name: $.from('name'),
                // m2mSysOverrides specifically maps to M2M table overrides
                m2mSysOverrides: $.from('sys_overrides').def(''),

                // Original tool details fields from tool record
                sysOverrides: toolRecord
                    ? $.val(toolRecord.get('sys_overrides')?.getValue()).def('')
                    : $.val(undefined),
                ...(toolRecord
                    ? {
                          recordType: $.val(
                              includeIfNotDefault(
                                  toolRecord.get('record_type')?.ifString()?.getValue() || 'template',
                                  'template'
                              )
                          ),
                      }
                    : {}),
                script: script ? $.val(script) : $.val(undefined),
                // Map target_document to type-specific property names based on tool type
                capabilityId: $.val(getTargetDocumentForType(toolType, 'capability', toolRecord)),
                subflowId: $.val(getTargetDocumentForType(toolType, 'subflow', toolRecord)),
                flowActionId: $.val(getTargetDocumentForType(toolType, 'action', toolRecord)),
                catalogItemId: $.val(getTargetDocumentForType(toolType, 'catalog', toolRecord)),
                virtualAgentId: $.val(getTargetDocumentForType(toolType, ['topic', 'topic_block'], toolRecord)),
                // For crud/script tools, include targetDocument; exclude OOB types (identified by type alone) and reference-type tools
                targetDocument: $.val(
                    toolType !== 'capability' &&
                        toolType !== 'subflow' &&
                        toolType !== 'action' &&
                        toolType !== 'catalog' &&
                        toolType !== 'topic' &&
                        toolType !== 'topic_block' &&
                        toolType !== 'web_automation' &&
                        toolType !== 'file_upload' &&
                        toolType !== 'knowledge_graph' &&
                        toolType !== 'rag'
                        ? includeIfNotDefault(toolRecord?.get('target_document')?.ifString()?.getValue(), '')
                        : undefined
                ),
                // Include type if we have it (either from tool record or detected from OOB map)
                // Omit the field if we couldn't determine the type
                type: toolType ? $.val(mapToolTypeToExternal(toolType)) : $.val(undefined),

                // Fields from M2M configuration
                documentStatus: $.from('document_status').def(''),
                displayOutput: $.from('display_output').toBoolean().def(false),
                executionMode: $.from('execution_mode'),
                inputs: $.from('inputs').map((v) => {
                    // Only include inputs for CRUD, script, and rag tools
                    if (toolType !== 'crud' && toolType !== 'script' && toolType !== 'rag') {
                        return undefined
                    }
                    // Use Shape's parseJson() utility method — guard against empty string
                    const str = v.ifString()?.getValue()
                    const parsed = str ? v.ifString()?.parseJson()?.getValue() : undefined
                    if (!parsed) {
                        return undefined
                    }
                    // Transform RAG inputs to RagInputType format
                    if (toolType === 'rag' && Array.isArray(parsed)) {
                        return transformRagInputs(parsed)
                    }
                    // Transform CRUD inputs to ToolInputType format
                    if (toolType === 'crud' && Array.isArray(parsed)) {
                        return transformCrudInputs(parsed)
                    }
                    // Normalize script inputs to only known ToolInputField fields.
                    // Raw DB data may contain unknown fields (e.g. 'metadata') and may
                    // omit required fields (e.g. 'mandatory') — both cause build errors.
                    const cleanedInputs = Array.isArray(parsed)
                        ? (parsed as { [key: string]: unknown }[]).map((input) => {
                              const type = input['type'] as string | undefined
                              const referenceConfig = input['referenceConfig'] as
                                  | { table: string; field: string }
                                  | undefined
                              // Build a normalized ToolInputField, omitting 'type' when it is
                              // the default 'string' to keep generated Fluent code clean.
                              const normalized: {
                                  name: string
                                  description: string
                                  mandatory: boolean
                                  value?: string
                                  invalidMessage?: string | null
                                  mappedToColumn?: string
                                  type?: string
                                  referenceConfig?: { table: string; field: string }
                              } = {
                                  name: (input['name'] as string) || '',
                                  description: (input['description'] as string) || '',
                                  mandatory: (input['mandatory'] as boolean | undefined) ?? false,
                              }
                              if (input['value'] !== undefined && input['value'] !== '') {
                                  normalized.value = input['value'] as string
                              }
                              if (input['invalidMessage'] !== undefined) {
                                  normalized.invalidMessage = input['invalidMessage'] as string | null
                              }
                              if (input['mappedToColumn'] !== undefined) {
                                  normalized.mappedToColumn = input['mappedToColumn'] as string
                              }
                              if (type !== undefined && type !== 'string') {
                                  normalized.type = type
                              }
                              if (referenceConfig !== undefined) {
                                  normalized.referenceConfig = referenceConfig
                              }
                              return normalized
                          })
                        : parsed
                    return includeIfNotDefault(cleanedInputs, [])
                }),
                maxAutoExecutions: $.val(maxAutoExecutionsValue),
                outputTransformationStrategy: $.from('output_transformation_strategy').def(''),
                postMessage: $.from('post_message').def(''),
                preMessage: $.from('pre_message').def(''),
                toolAttributes: $.from('tool_attributes').map((v) => {
                    // Use Shape's parseJson() utility method — guard against empty string
                    const str = v.ifString()?.getValue()
                    const parsed = str ? v.ifString()?.parseJson()?.getValue() : undefined
                    if (!parsed || (typeof parsed === 'object' && Object.keys(parsed).length === 0)) {
                        return undefined
                    }
                    return includeIfNotDefault(parsed, {})
                }),
                timeout: $.from('timeout').def(''),
                preRun: $.from('pre_run').toBoolean().def(false),
                transformationInstructions: $.from('transformation_instructions').def(''),
                postProcessingScript: $.from('post_processing_script').def(''),
                widgets: $.from('widgets').map((v) => {
                    const val = v.ifString()?.getValue()
                    if (!val || val === '') {
                        return undefined
                    }
                    const widgetArray = val.split(',').filter((w) => w.trim() !== '')
                    return includeIfNotDefault(widgetArray, [])
                }),
            }))
        })
    )
}

/**
 * Helper function to compute flattened agent config values from config record
 * Returns an object with the flattened fields to be spread into the main agent shape
 */
export const computeAgentConfigValue = (config: Record | undefined) => {
    if (!config) {
        return undefined
    }

    const publicBool = config.get('public')?.toBoolean()?.getValue()
    const agentLearningBool = config.get('agent_learning')?.toBoolean()?.getValue()

    const docUrlValue = config.get('doc_url')?.getValue()
    const iconUrlValue = config.get('icon_url')?.getValue()
    const runAsUserValue = config.get('run_as_user')?.getValue()
    const agentConfigSysOverridesValue = config.get('sys_overrides')?.getValue()
    const agentDescriptorValue = config.get('agent_descriptor')?.getValue()

    return {
        public: publicBool || undefined,
        agentLearning: agentLearningBool || undefined,
        docUrl: docUrlValue === '' ? undefined : docUrlValue,
        iconUrl: iconUrlValue === '' ? undefined : iconUrlValue,
        runAsUser: runAsUserValue || '', // Keep empty string as default
        agentConfigSysOverrides: agentConfigSysOverridesValue === '' ? undefined : agentConfigSysOverridesValue,
        agentDescriptor: agentDescriptorValue === '' ? undefined : agentDescriptorValue,
    }
}

/**
 * Helper function to compute version details value from version records
 */
export const computeVersionDetailsValue = (versions: Record[]) => {
    if (!versions || versions.length === 0) {
        return undefined
    }
    return versions.map((version) =>
        version.transform(({ $ }) => ({
            condition: $.from('condition').def(''),
            state: $.from('state'),
            name: $.from('version_name'),
            number: $.from('version_number').toNumber().def(1),
            instructions: $.from('instructions'),
        }))
    )
}

export { computeDataAccessValue } from '../shared/data-access-transform-helper'

/**
 * Helper function to reconstruct a securityAcl value from a sys_security_acl record
 * and its associated sys_security_acl_role records.
 *
 * Used in toShape to reverse-map ACL records back to the SecurityAclUserAccessConfig API shape.
 * Returns undefined if no ACL record is provided (caller should fall back to raw acl field).
 *
 * @param aclRecord      - The sys_security_acl record found for this agent
 * @param aclRoleRecords - All sys_security_acl_role records linked to that ACL
 */
export const computeSecurityAclValue = (aclRecord: Record | undefined, aclRoleRecords: Record[]) => {
    if (!aclRecord) {
        return undefined
    }

    // Collect role values from the ACL role records (prefer sys_id string),
    // excluding the sentinel public-access role which signals type='Public'
    const allRoles = aclRoleRecords
        .map((r) => {
            const roleRef = r.get('sys_user_role')
            if (roleRef?.isRecord()) {
                return roleRef.getId().getValue()
            }
            return roleRef?.ifString()?.getValue()
        })
        .filter((v): v is string => !!v)

    const isPublic = allRoles.length > 0 && allRoles.every((r) => r === PUBLIC_ACCESS_ROLE_SYS_ID)
    const roles = allRoles.filter((r) => r !== PUBLIC_ACCESS_ROLE_SYS_ID)

    const $id = NowIdShape.from(aclRecord)

    if (isPublic) {
        return {
            $id,
            type: 'Public' as const,
        }
    }

    if (roles.length > 0) {
        return {
            $id,
            type: 'Specific role' as const,
            roles,
        }
    }

    return {
        $id,
        type: 'Any authenticated user' as const,
    }
}

/**
 * Helper function to map trigger config records to trigger config objects
 * @param triggerConfigs - Array of trigger configuration records
 * @param triggerMappings - Array of trigger mapping records (M2M table)
 */
export const mapTriggerConfigs = (triggerConfigs: Record[], triggerMappings: Record[]) => {
    if (triggerConfigs.length === 0) {
        return undefined
    }

    return triggerConfigs.map((tc) => {
        // Find the corresponding trigger mapping record for this trigger config
        const triggerConfigId = tc.getId().getValue()
        const triggerMapping = triggerMappings.find((tm) => {
            const tmTriggerConfig = tm.get('trigger_configuration')
            if (tmTriggerConfig?.isRecord()) {
                return tmTriggerConfig.getId().getValue() === triggerConfigId
            }
            return tmTriggerConfig?.ifString()?.getValue() === triggerConfigId
        })

        // Build schedule object from flat DB fields
        const runDayOfMonthRaw = tc.get('run_dayofmonth')?.ifString()?.getValue()
        const runDayOfMonth = runDayOfMonthRaw ? Number(runDayOfMonthRaw) : undefined
        const runDayOfWeekRaw = tc.get('run_dayofweek')?.ifString()?.getValue()
        const runDayOfWeek = runDayOfWeekRaw ? Number(runDayOfWeekRaw) : undefined
        const repeatInterval = tc.get('run_period')?.ifString()?.getValue()
        const starting = tc.get('run_start')?.ifString()?.getValue()
        const time = tc.get('run_time')?.ifString()?.getValue()
        const triggerStrategy = tc.get('trigger_strategy')?.ifString()?.getValue()

        const schedule =
            runDayOfMonth || runDayOfWeek || repeatInterval || starting || time || triggerStrategy
                ? {
                      ...(runDayOfMonth && { runDayOfMonth }),
                      ...(runDayOfWeek && { runDayOfWeek }),
                      ...(repeatInterval && { repeatInterval }),
                      ...(starting && { starting }),
                      ...(time && { time }),
                      ...(triggerStrategy && { triggerStrategy }),
                  }
                : undefined

        // Reverse-map channel sys_id to OOB name
        const channelSysId = tc.get('channel')?.ifString()?.getValue()
        const channelValue = channelSysId ? CHANNEL_SYS_ID_TO_NAME_MAP[channelSysId] || channelSysId : undefined

        // Exclude portal when it holds the literal string 'undefined' or is empty
        const portalValue = tc.get('portal')?.ifString()?.getValue()
        const includePortal = Boolean(portalValue && portalValue !== 'undefined')

        return tc.transform(({ $ }) => ({
            name: $.from('name'),
            description: $.from('description').def(''),
            active: $.from('active').toBoolean().def(true),
            businessRule: $.from('business_rule').def(''),
            channel: $.val(channelValue),
            triggerCondition: $.from('condition').def(''),
            enableDiscovery: $.from('enable_discovery').toBoolean().def(false),
            notificationScript: $.from('notification_script').def(''),
            objectiveTemplate: $.from('objective_template'),
            ...(includePortal ? { portal: $.from('portal') } : {}),
            profile: $.from('profile').def(''),
            runAs: $.from('run_as').def(''),
            runAsScript: $.from('run_as_script').def(''),
            runAsUser: $.from('run_as_user').def(''),
            schedule: $.val(schedule),
            showNotifications: $.from('show_notifications').toBoolean().def(false),
            domain: $.from('sys_domain').def('global'),
            sysOverrides: $.from('sys_overrides').def(''),
            targetTable: $.from('target_table'),
            triggerFlow: $.from('trigger_flow').def(''),
            triggerFlowDefinitionType: $.from('trigger_flow_definition_type'),
            usecase: $.from('usecase').def(''),
            usecaseDiscovery: $.from('usecase_discovery').toBoolean().def(false),
            // Get m2mSysOverrides from the trigger mapping M2M record
            m2mSysOverrides: $.val(triggerMapping?.get('sys_overrides')),
        }))
    })
}
